import { useEffect, useRef, useState } from "react";
import { TProcessStates } from "../../../../lib/types/general";
import { TEmployeeRole } from "../../../../lib/types/employee_roles";
import { PositionsCheckListEmployeeRoleBackOffice } from "../../../common/positions-check-list";
import {
    TAddEmployeeRole,
    TSearchSkillsAddRoleModal,
} from "../../../../services/backoffice/backoffice-service";
import { Spinner } from "../../../common/spinner";
import {
    TSkills,
    TSkillsFilters,
    TSkillsResults,
} from "../../../../lib/types/skills";

const RoleRow = ({
    role,
    index,
    positions,
    updateRoleHandler,
    searchName,
    getAllSkillsForEmployeeRoles,
    addIndustryRole,
    updateSkillName,
    editingRoleId,
    setEditingRoleId,
}: {
    role: TEmployeeRole;
    index: number;
    positions: TSkills[];
    updateRoleHandler: (
        role_id: number,
        role_name?: string,
        skills_id?: number[],
        searchName?: string
    ) => void;
    searchName: string;
    getAllSkillsForEmployeeRoles: (
        offset: number,
        filterProps: TSkillsFilters
    ) => Promise<TSkillsResults>;
    addIndustryRole: (v: string) => void;
    updateSkillName: (
        posId: string,
        newName: string,
        editableSkillsCallback?: React.Dispatch<React.SetStateAction<TSkills[]>>
    ) => void;
    editingRoleId: number | undefined;
    setEditingRoleId: (id: number | undefined) => void;
}) => {
    const roleEditNameRef = useRef<HTMLInputElement>(null);
    const [refSkills, setRefSkills] = useState<Record<number, boolean>>({});
    const roleRowId = `collapseRole_${role.id}`;
    const [emptyNameError, setEmptyNameError] = useState(false);
    const [minSkillError, setMinSkillError] = useState(false);
    const [isCollapsedOpen, setIsCollapsedOpen] = useState(false);
    const [editableSkills, setEditableSkills] = useState<TSkills[]>([]);
    const [searchSkillName, setSearchSkillName] = useState<string>("");
    const [isSearchingSkill, setIsSearchingSkill] = useState(false);
    const [skillNotFound, setSkillNotFound] = useState(false);
    const positionNameRef = useRef<HTMLInputElement>(null);
    const [skillExistsInPositionList, setSkillExistsInPositionList] =
        useState(false);
    const [emptySkillInputError, setEmptySkillInputError] = useState(false);
    const isEditing = editingRoleId === role.id;
    const [addingNewSkillInRow, setAddingNewSkillInRow] = useState(false);
    const [savingNewRoleName, setSavingNewRoleName] = useState(false);

    const editEmployeeRoleHandler = async (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        const selectedSkills: { [id: number]: boolean } = {};
        role.skills.forEach((skill) => {
            selectedSkills[skill.id] = true;
        });

        setRefSkills(selectedSkills);
        const result = await getAllSkillsForEmployeeRoles(0, { name: "" });
        setEditableSkills(result.data.skillsData);
        setEditingRoleId(role.id);
        setIsCollapsedOpen(true);
    };

    const saveEmployeeRoleHandler = async () => {
        const inputName = roleEditNameRef.current?.value?.trim() || "";

        if (inputName.length === 0) {
            setEmptyNameError(true);
            return;
        }

        const selectedSkills: number[] = Object.entries(refSkills)
            .filter(([_, checked]) => checked)
            .map(([id]) => Number(id));

        if (selectedSkills.length === 0) {
            setMinSkillError(true);
            return;
        }
        setSavingNewRoleName(true);
        await updateRoleHandler(role.id, inputName, selectedSkills, searchName);
        setSavingNewRoleName(false);
        setEmptyNameError(false);
        setMinSkillError(false);
        setEditingRoleId(undefined);
        setIsCollapsedOpen(false);
    };

    const searchSkillByNameHandler = (skillNameToSearch: string) => {
        setEmptySkillInputError(false);

        if (isSearchingSkill) return;
        setIsSearchingSkill(true);

        const filteredSkills = positions.filter((skill) =>
            skill.name.toLowerCase().includes(skillNameToSearch.toLowerCase())
        );

        if (filteredSkills.length === 0) {
            setSkillNotFound(true);
        } else {
            setSkillNotFound(false);
        }

        setEditableSkills(filteredSkills);
        setIsSearchingSkill(false);
    };

    const applyFiltersHandler = () => {
        if (!searchSkillName.trim()) return;
        searchSkillByNameHandler(searchSkillName);
    };

    const resetFiltersHandler = async () => {
        const result = await getAllSkillsForEmployeeRoles(0, { name: "" });
        setEditableSkills(result.data.skillsData);
        setSearchSkillName("");
        searchSkillByNameHandler("");
        setMinSkillError(false);
    };

    const handlePositionAdded = async (value: string) => {
        if (!value.trim()) return;
        setAddingNewSkillInRow(true);
        await addIndustryRole(value);
        setAddingNewSkillInRow(false);
    };

    const addNewSkillInSkillSectionHandler = async () => {
        const newSkill = positionNameRef.current?.value;

        if (!newSkill) {
            setEmptySkillInputError(true);
            return;
        }

        const skillAlreadyExists = positions.some(
            (pos) => pos.name.toLowerCase() === newSkill.trim().toLowerCase()
        );

        if (skillAlreadyExists) {
            setSkillExistsInPositionList(true);
            return;
        }

        await handlePositionAdded(newSkill);
        await resetFiltersHandler();

        setSkillExistsInPositionList(false);
        setEmptySkillInputError(false);
        positionNameRef.current.value = "";
    };

    const editSkillNameHandleLocal = async (posId: string, newName: string) => {
        await updateSkillName(posId, newName, setEditableSkills);
    };

    return (
        <>
            <tr className="table-light align-middle">
                <th scope="row">{index}</th>
                {!isEditing && (
                    <>
                        <td>{role.name}</td>
                        <td className="fw-bold" style={{ width: "40%" }}>
                            <button
                                className="btn btn-sm btn-primary rounded-3"
                                onClick={() =>
                                    setIsCollapsedOpen(!isCollapsedOpen)
                                }
                            >
                                <i className="fa-regular fa-eye fa-xs me-2"></i>
                                {role.skills_count}
                            </button>
                        </td>
                        <td className="text-center">
                            <button
                                className="btn btn-sm btn-light rounded-3"
                                onClick={(e: any) => editEmployeeRoleHandler(e)}
                            >
                                <i className="fa-regular fa-pen-to-square fa-xs"></i>
                            </button>
                        </td>
                    </>
                )}
                {isEditing && (
                    <>
                        <td style={{ width: "50%" }}>
                            <input
                                type="text"
                                ref={roleEditNameRef}
                                className={`form-control ${
                                    emptyNameError ? "is-invalid" : ""
                                }`}
                                defaultValue={role.name}
                                aria-label={role.name}
                            />
                        </td>
                        <td className="text-center text-nowrap">
                            <button
                                type="button"
                                className="btn btn-primary btn-sm me-2"
                                onClick={saveEmployeeRoleHandler}
                            >
                                {!savingNewRoleName && <span>Save</span>}
                                {savingNewRoleName && <Spinner />}
                            </button>
                            <button
                                type="button"
                                className="btn btn-danger btn-sm"
                                onClick={() => {
                                    resetFiltersHandler();
                                    setEmptyNameError(false);
                                    setMinSkillError(false);
                                    setEditingRoleId(undefined);
                                    setIsCollapsedOpen(false);
                                    setEmptySkillInputError(false);
                                }}
                                disabled={savingNewRoleName}
                            >
                                Cancel
                            </button>
                        </td>
                        <td className="text-center">
                            <button
                                className="btn btn-sm btn-light rounded-3"
                                disabled
                            >
                                <i className="fa-regular fa-pen-to-square fa-xs"></i>
                            </button>
                        </td>
                    </>
                )}
            </tr>

            <tr
                className={`p-0 collapse-horizontal collapse ${
                    isCollapsedOpen ? " show" : ""
                }`}
                id={roleRowId}
            >
                <td colSpan={4} className="p-0">
                    <table className="table table-striped-columns m-0">
                        <tbody>
                            <tr>
                                {!isEditing && (
                                    <>
                                        <td></td>
                                        <td style={{ width: "50%" }}>
                                            <div className="employeeRolesTable employeeRolesTable_addSkillsToRole">
                                                {role.skills.map((skill) => {
                                                    return (
                                                        <p
                                                            key={`skill_id_row_notEditing_${skill.id}_${index}`}
                                                            className="mb-2 ps-2"
                                                        >
                                                            {skill.name}
                                                        </p>
                                                    );
                                                })}
                                            </div>
                                        </td>{" "}
                                    </>
                                )}
                                {isEditing && (
                                    <>
                                        <td className="p-3">
                                            {minSkillError && (
                                                <div
                                                    className="alert alert-danger text-danger py-2 px-3 rounded-3"
                                                    role="alert"
                                                >
                                                    <i className="fa-solid fa-circle-exclamation me-2"></i>
                                                    <small>
                                                        Select at least one
                                                        skill.
                                                    </small>
                                                </div>
                                            )}
                                            <div className="employeeRolesTable employeeRolesTable_editableRoleRow container">
                                                <div className="row">
                                                    <div className="col-lg-6">
                                                        <div className="input-group mt-2 mb-3 position-relative">
                                                            <span className="input-group-text rounded-end-0 rounded-start-3">
                                                                Skills
                                                            </span>
                                                            <input
                                                                type="text"
                                                                className="form-control rounded-0"
                                                                placeholder="Search Skills"
                                                                value={
                                                                    searchSkillName
                                                                }
                                                                onChange={(e) =>
                                                                    setSearchSkillName(
                                                                        e.target
                                                                            .value
                                                                    )
                                                                }
                                                            />
                                                            <button
                                                                className="btn btn-light border rounded-start-0 rounded-end-3"
                                                                type="button"
                                                                id="Search-Skills-Input-Employee-Role-Modal"
                                                                onClick={
                                                                    applyFiltersHandler
                                                                }
                                                            >
                                                                <i className="fa-solid fa-magnifying-glass me-2"></i>
                                                                <span className="d-lg-inline d-none">
                                                                    Search
                                                                </span>
                                                            </button>
                                                            <button
                                                                className="btn btn-light text-primary border-primary rounded-3 ms-2"
                                                                onClick={
                                                                    resetFiltersHandler
                                                                }
                                                            >
                                                                {" "}
                                                                <i className="fa-solid fa-arrows-rotate"></i>
                                                            </button>
                                                        </div>
                                                        {skillNotFound && (
                                                            <>
                                                                <div className="text-danger mb-3">
                                                                    <i className="fa-solid fa-circle-exclamation me-2"></i>
                                                                    <small>
                                                                        Skill
                                                                        not
                                                                        found.
                                                                    </small>
                                                                </div>
                                                            </>
                                                        )}
                                                    </div>
                                                    <div className="col-lg-6">
                                                        {skillNotFound && (
                                                            <div className="bg-light rounded-3 p-4">
                                                                <p className="fw-bold">
                                                                    <small>
                                                                        Would
                                                                        you like
                                                                        to add a
                                                                        new
                                                                        skill?
                                                                    </small>
                                                                </p>
                                                                <div className="input-group">
                                                                    <input
                                                                        type="text"
                                                                        ref={
                                                                            positionNameRef
                                                                        }
                                                                        className={`form-control rounded-end-0 rounded-start-3 ${
                                                                            emptySkillInputError
                                                                                ? "is-invalid"
                                                                                : ""
                                                                        }`}
                                                                        placeholder="Add new skill"
                                                                        aria-label="Add new skill"
                                                                        aria-describedby="add-new-skill-in-skill-section-btn"
                                                                        disabled={
                                                                            addingNewSkillInRow
                                                                        }
                                                                        defaultValue={
                                                                            searchSkillName ||
                                                                            ""
                                                                        }
                                                                    />
                                                                    <div className="input-group-append">
                                                                        <button
                                                                            className="btn btn-light text-primary border-primary rounded-start-0 rounded-end-3"
                                                                            type="button"
                                                                            id="add-new-skill-in-skill-section-btn"
                                                                            onClick={
                                                                                addNewSkillInSkillSectionHandler
                                                                            }
                                                                            disabled={
                                                                                addingNewSkillInRow
                                                                            }
                                                                        >
                                                                            {addingNewSkillInRow && (
                                                                                <Spinner spinnerColor="text-primary" />
                                                                            )}
                                                                            {!addingNewSkillInRow && (
                                                                                <>
                                                                                    <i className="fa-solid fa-circle-plus me-2"></i>
                                                                                    <span className="d-lg-inline d-none">
                                                                                        Add
                                                                                        New
                                                                                        Skill
                                                                                    </span>
                                                                                    <span className="d-inline d-lg-none">
                                                                                        Add
                                                                                    </span>
                                                                                </>
                                                                            )}
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                                {skillExistsInPositionList && (
                                                                    <div
                                                                        className="alert alert-danger text-danger py-2 px-3 rounded-3 my-2"
                                                                        role="alert"
                                                                    >
                                                                        <i className="fa-solid fa-circle-exclamation me-2"></i>
                                                                        <small>
                                                                            {
                                                                                positionNameRef
                                                                                    .current
                                                                                    ?.value
                                                                            }{" "}
                                                                            already
                                                                            exists.
                                                                        </small>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                {!skillNotFound && (
                                                    <PositionsCheckListEmployeeRoleBackOffice
                                                        skillList={
                                                            editableSkills
                                                        }
                                                        checkedList={refSkills}
                                                        isInModal={false}
                                                        setRefSkills={
                                                            setRefSkills
                                                        }
                                                        updateSkillName={
                                                            editSkillNameHandleLocal
                                                        }
                                                    />
                                                )}
                                            </div>
                                        </td>{" "}
                                    </>
                                )}
                            </tr>
                        </tbody>
                    </table>
                </td>
            </tr>
        </>
    );
};

const EmployeeRolesTable = ({
    roles,
    positions,
    updateRoleHandler,
    searchName,
    getAllSkillsForEmployeeRoles,
    addIndustryRole,
    updateSkillName,
    editingRoleId,
    setEditingRoleId,
}: {
    roles: TEmployeeRole[];
    positions: TSkills[];
    updateRoleHandler: (
        role_id: number,
        role_name?: string,
        skills_id?: number[]
    ) => void;
    searchName: string;
    getAllSkillsForEmployeeRoles: (
        offset: number,
        filterProps: TSkillsFilters
    ) => Promise<TSkillsResults>;
    addIndustryRole: (v: string) => void;
    updateSkillName: (posId: string, newName: string) => void;
    editingRoleId: number | undefined;
    setEditingRoleId: (id: number | undefined) => void;
}) => {
    return (
        <div className="table-responsive rounded-3 employeeRolesTable mb-2">
            <table className="table table-striped-columns table-hover table-bordered m-0">
                <thead className="table-dark align-middle sticky-top">
                    <tr>
                        <th scope="col"></th>
                        <th scope="col">Employee Role</th>
                        <th scope="col">Skills</th>
                        <th scope="col"></th>
                    </tr>
                </thead>
                <tbody>
                    {roles.map((role, index) => (
                        <RoleRow
                            key={index}
                            role={role}
                            index={index + 1}
                            positions={positions}
                            updateRoleHandler={updateRoleHandler}
                            searchName={searchName}
                            getAllSkillsForEmployeeRoles={
                                getAllSkillsForEmployeeRoles
                            }
                            addIndustryRole={addIndustryRole}
                            updateSkillName={updateSkillName}
                            editingRoleId={editingRoleId}
                            setEditingRoleId={setEditingRoleId}
                        />
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export const EmployeeRolesAdmin = ({
    employee_roles,
    positions,
    updateRoleHandler,
    searchName,
    getAllSkillsForEmployeeRoles,
    addIndustryRole,
    updateSkillName,
    editingRoleId,
    setEditingRoleId,
}: {
    employee_roles: TEmployeeRole[];
    positions: TSkills[];
    updateRoleHandler: (
        role_id: number,
        role_name?: string,
        skills_id?: number[],
        searchName?: string
    ) => void;
    searchName: string;
    getAllSkillsForEmployeeRoles: (
        offset: number,
        filterProps: TSkillsFilters
    ) => Promise<TSkillsResults>;
    addIndustryRole: (v: string) => void;
    updateSkillName: (posId: string, newName: string) => void;
    editingRoleId: number | undefined;
    setEditingRoleId: (id: number | undefined) => void;
}) => {
    return (
        <>
            <EmployeeRolesTable
                roles={employee_roles}
                positions={positions}
                updateRoleHandler={updateRoleHandler}
                searchName={searchName}
                getAllSkillsForEmployeeRoles={getAllSkillsForEmployeeRoles}
                addIndustryRole={addIndustryRole}
                updateSkillName={updateSkillName}
                editingRoleId={editingRoleId}
                setEditingRoleId={setEditingRoleId}
            />
        </>
    );
};

export const AddEmployeeRoleModal = ({
    openAddEmployeeRoleModal,
    onClose,
    addNewEmployeeRoleHandler,
    allSkills,
    addNewSkillHandler,
    addingSkillState,
    getSkillListWithSearchFilterInAddModal,
    setAllSkills,
}: {
    openAddEmployeeRoleModal: boolean;
    onClose: () => void;
    addNewEmployeeRoleHandler: (props: TAddEmployeeRole) => void;
    allSkills: TSkills[];
    addNewSkillHandler: (value: string) => void;
    addingSkillState: TProcessStates;
    getSkillListWithSearchFilterInAddModal: (
        offset: number,
        props: TSearchSkillsAddRoleModal
    ) => Promise<TSkillsResults>;
    setAllSkills: (value: TSkills[]) => void;
}) => {
    const inputRoleNameRef = useRef<HTMLInputElement>(null);
    const [refSelectSkillsUpdateRole, setRefSelectSkillsUpdateRole] = useState<
        Record<number, boolean>
    >({});
    const newSkillInRoleModalRef = useRef<HTMLInputElement>(null);
    const [emptyNameError, setEmptyNameError] = useState(false);
    const [minSkillError, setMinSkillError] = useState(false);
    const [emptySkillNameError, setEmptySkillNameError] = useState(false);
    const [skillExistsInRoleModal, setSkillExistsInRoleModal] = useState(false);
    const [newSkillAdded, setNewSkillAdded] = useState(false);
    const [searchSkillName, setSearchSkillName] = useState<string>("");
    const [isSearchingSkillInModal, setIsSearchingSkillInModal] =
        useState(false);
    const [modalSkills, setModalSkills] = useState<TSkills[]>([]);
    const [addingNewSkill, setAddingNewSkill] = useState(false);
    const [addingNewRole, setAddingNewRole] = useState(false);

    const handleAddNewRole = async () => {
        const newRoleName = inputRoleNameRef.current?.value;

        if (!newRoleName) {
            setEmptyNameError(true);
            return;
        }

        const selectedSkills: number[] = Object.entries(
            refSelectSkillsUpdateRole
        )
            .filter(([_, checked]) => checked)
            .map(([id]) => Number(id));

        if (selectedSkills.length === 0) {
            setMinSkillError(true);
            return;
        }
        setAddingNewRole(true);
        await addNewEmployeeRoleHandler({
            employee_role: newRoleName,
            skill_ids: selectedSkills,
        });
        inputRoleNameRef.current.value = "";
        setAddingNewRole(false);
        setEmptyNameError(false);
        setMinSkillError(false);
        onClose();
    };

    const addNewSkillInRoleModalHandler = async () => {
        const newSkill = newSkillInRoleModalRef.current?.value;
        if (!newSkill) {
            setEmptySkillNameError(true);
            return;
        }

        const skillAlreadyExists = allSkills.some(
            (pos) => pos.name.toLowerCase() === newSkill.trim().toLowerCase()
        );

        if (skillAlreadyExists) {
            setSkillExistsInRoleModal(true);
            setNewSkillAdded(false);
            return;
        }
        setSkillExistsInRoleModal(false);
        setAddingNewSkill(true);
        await addNewSkillHandler(newSkill);
        const allSkillsResult = await getSkillListWithSearchFilterInAddModal(
            0,
            {
                name: "",
            }
        );
        setAllSkills(allSkillsResult.data.skillsData);
        setModalSkills(allSkillsResult.data.skillsData);
        newSkillInRoleModalRef.current.value = "";
        setAddingNewSkill(false);
        setNewSkillAdded(true);
        setEmptySkillNameError(false);
    };

    const searchSkillByNameHandler = async (skillNameToSearch: string) => {
        if (isSearchingSkillInModal) return;
        setSkillExistsInRoleModal(false);
        setIsSearchingSkillInModal(true);

        const result = await getSkillListWithSearchFilterInAddModal(0, {
            name: skillNameToSearch.trim(),
        });

        setModalSkills(result.data.skillsData);
        setIsSearchingSkillInModal(false);
    };

    const applyFiltersHandler = () => {
        searchSkillByNameHandler(searchSkillName);
    };

    useEffect(() => {
        searchSkillByNameHandler("");
        setNewSkillAdded(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            <div
                className={`modal fade ${
                    openAddEmployeeRoleModal ? "show d-block" : ""
                }`}
                tabIndex={-1}
                role="dialog"
            >
                {" "}
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h1 className="modal-title fs-5">
                                Add New Employee Role
                            </h1>
                            <button
                                type="button"
                                className="btn-close"
                                onClick={onClose}
                                aria-label="Close"
                            ></button>
                        </div>

                        <div className="modal-body">
                            <div className="input-group mb-4">
                                <span className="input-group-text rounded-end-0 rounded-start-3">
                                    Name
                                </span>
                                <input
                                    type="text"
                                    className={`form-control rounded-start-0 rounded-end-3 ${
                                        emptyNameError ? "is-invalid" : ""
                                    }`}
                                    placeholder="Role Name"
                                    aria-label="Role Name"
                                    aria-describedby="New-Role-Name-Input"
                                    ref={inputRoleNameRef}
                                />
                            </div>

                            <div>
                                <label className="form-label ms-1 fw-bold">
                                    Select at least one skill:
                                </label>
                                <div className="input-group mb-3 position-relative">
                                    <span className="input-group-text rounded-end-0 rounded-start-3">
                                        Skills
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control rounded-0"
                                        placeholder="Search Skills"
                                        value={searchSkillName}
                                        onChange={(e) =>
                                            setSearchSkillName(e.target.value)
                                        }
                                    />
                                    <button
                                        className="btn btn-light border rounded-start-0 rounded-end-3"
                                        type="button"
                                        id="Search-Skills-Input-Employee-Role-Modal"
                                        onClick={applyFiltersHandler}
                                    >
                                        <i className="fa-solid fa-magnifying-glass me-2"></i>
                                        Search
                                    </button>
                                </div>
                                {minSkillError && (
                                    <div
                                        className="alert alert-danger text-danger py-2 px-3 rounded-3 my-2"
                                        role="alert"
                                    >
                                        <i className="fa-solid fa-circle-exclamation me-2"></i>
                                        <small>
                                            Select at least one skill.
                                        </small>
                                    </div>
                                )}

                                <div className="employeeRolesTable employeeRolesTable_addSkillsToRole container">
                                    {isSearchingSkillInModal && (
                                        <Spinner spinnerColor="text-primary" />
                                    )}

                                    {!isSearchingSkillInModal && (
                                        <>
                                            <PositionsCheckListEmployeeRoleBackOffice
                                                skillList={modalSkills}
                                                checkedList={
                                                    refSelectSkillsUpdateRole
                                                }
                                                isInModal={true}
                                                setRefSkills={
                                                    setRefSelectSkillsUpdateRole
                                                }
                                            />
                                        </>
                                    )}
                                </div>
                            </div>
                            <hr />
                            <div className="bg-light rounded-3 p-2">
                                <label className="form-label ms-1 fw-bold">
                                    {" "}
                                    <small>
                                        Can't find the skill you need?
                                    </small>
                                </label>
                                {skillExistsInRoleModal && (
                                    <div
                                        className="alert alert-danger text-danger py-2 px-3 rounded-3 my-2"
                                        role="alert"
                                    >
                                        <i className="fa-solid fa-circle-exclamation me-2"></i>
                                        <small>
                                            {
                                                newSkillInRoleModalRef.current
                                                    ?.value
                                            }{" "}
                                            already exists.
                                        </small>
                                    </div>
                                )}
                                <div className="input-group mb-3">
                                    <input
                                        type="text"
                                        className={`form-control  rounded-end-0 rounded-start-3 ${
                                            emptySkillNameError
                                                ? "is-invalid"
                                                : ""
                                        }`}
                                        placeholder="Add New Skill"
                                        aria-label="Add New Skill"
                                        aria-describedby="add-new-skill-btn-in-role-modal"
                                        ref={newSkillInRoleModalRef}
                                    />
                                    <button
                                        className="btn btn-outline-primary rounded-start-0 rounded-end-3"
                                        type="button"
                                        id="add-new-skill-btn-in-role-modal"
                                        onClick={addNewSkillInRoleModalHandler}
                                        disabled={
                                            addingSkillState === "working"
                                        }
                                    >
                                        {!addingNewSkill && (
                                            <span>Add Skill</span>
                                        )}
                                        {addingNewSkill && (
                                            <Spinner spinnerColor="text-primary" />
                                        )}
                                    </button>
                                </div>
                                <div className="text-center">
                                    {newSkillAdded && (
                                        <label className="text-secondary">
                                            New Skill Added!
                                        </label>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-danger rounded-3"
                                onClick={onClose}
                                disabled={addingNewRole}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary px-4 rounded-3"
                                onClick={handleAddNewRole}
                            >
                                {!addingNewRole && <span>Save</span>}
                                {addingNewRole && <Spinner />}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            {openAddEmployeeRoleModal && (
                <div className="modal-backdrop fade show"></div>
            )}
        </>
    );
};
